<script setup lang="ts">
import { SancareDatePicker } from '@sancare/typescript-commons'
import { capitalize, lowerFirst } from '@sancare/typescript-commons/src/misc/string-utils'
import { FilterDataValue, FilterEvent } from '@sancare/typescript-commons/src/types/filters'
import dayjs, { Dayjs } from 'dayjs'
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { FilterData } from '@/store/modules/filters/types'

import { optionToValue } from './utils'

const props = withDefaults(defineProps<{
  filterData: FilterData
  isLegacy?: boolean
  initialValue?: FilterDataValue | null
}>(), {
  initialValue: null,
  isLegacy: true
})

const emit = defineEmits<{
  'add-filter': [payload: FilterEvent]
}>()

const { t } = useI18n()

const subFiltersValues = ref<Record<string, string>>({})

if (props.filterData.subFilters) {
  props.filterData.subFilters.forEach((filter) => {
    if (filter.default) {
      subFiltersValues.value[filter.name] = filter.default
    } else {
      subFiltersValues.value[filter.name] = filter.options ? optionToValue(filter.options[0]) : ''
    }
  })
}

const date = ref<Dayjs>(
  dayjs.isDayjs(props.filterData.default) ? props.filterData.default : dayjs(props.filterData.default)
)

function addFilter() {
  const name = props.filterData.name
  let type = props.filterData.format

  const value = date.value.format('YYYY-MM-DD')

  type = type.replace('{filterType}', capitalize(props.filterData.name))

  if (props.filterData.subFilters) {
    props.filterData.subFilters.forEach((filter) => {
      type = type.replace(`{${filter.name}}`, capitalize(subFiltersValues.value[filter.name]))
    })
  }

  type = lowerFirst(type)
  emit('add-filter', { name, type, value })
}

function initializeSubFiltersDefaults() {
  const values: Record<string, string> = {}

  if (props.filterData.subFilters) {
    props.filterData.subFilters.forEach((filter) => {
      if (filter.default) {
        values[filter.name] = filter.default
      } else if (filter.options?.length) {
        values[filter.name] = optionToValue(filter.options[0])
      } else {
        values[filter.name] = ''
      }
    })
  }

  subFiltersValues.value = values
}

function initializeFromInitialValue() {
  if (!props.initialValue) return

  if (props.initialValue.value) {
    date.value = dayjs(props.initialValue.value)
  }

  if (props.initialValue.subFilters) {
    subFiltersValues.value = { ...props.initialValue.subFilters }
  }
}

function initializeDateDefault() {
  date.value = dayjs.isDayjs(props.filterData.default)
    ? props.filterData.default
    : dayjs(props.filterData.default)
}

onMounted(() => {
  if (props.initialValue) {
    initializeFromInitialValue()
  } else {
    initializeSubFiltersDefaults()
    initializeDateDefault()
  }
})

watch(
  () => props.initialValue,
  (val) => {
    if (val) {
      initializeFromInitialValue()
    }
  }
)
</script>

<template>
  <div class="row gap-2 g-0">
    <div
      v-if="filterData && filterData.subFilters && filterData.subFilters.length"
      :class="isLegacy ? ['col-md-4 col-12'] : ['col-12']"
    >
      <select
        v-for="filter in filterData.subFilters"
        :id="`date-filter-sub-${filter.name}`"
        :key="filter.name"
        v-model="subFiltersValues[filter.name]"
        class="form-select"
      >
        <option
          v-for="opt in filter.options"
          :key="opt"
          :value="opt"
        >
          {{ filter.labels[opt] ? filter.labels[opt] : opt }}
        </option>
      </select>
    </div>
    <div
      class="justify-content-center justify-content-md-start ms-5 d-flex flex-row"
      :class="isLegacy ? ['col'] : ['col-12']"
    >
      <sancare-date-picker v-model="date" />
      <div class="offset-1">
        <button
          id="date-filter-add"
          class="btn btn-primary"
          type="button"
          @click="addFilter"
        >
          {{ t('ok') }}
        </button>
      </div>
    </div>
  </div>
</template>
